import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks/redux';
import {
  SectionHeader,
  NumberInput,
  Checkbox,
  Modal,
  InputField,
  Select,
  SelectOption,
  SelectProps,
  Button,
} from '@ets/design-system';

import { closeModal } from '_common/modals/ModalsSlice';

import EditorManager from 'Editor/services/EditorManager';

import styles from './CustomSpacingModal.module.scss';
import { useMemo, useState } from 'react';
import { MeasureInput } from '_common/components';

type LineRule = 'auto' | 'atLeast' | 'exact';

type SpacingTypeOption = SelectOption & {
  value: '1' | '1.15' | '1.5' | '2' | LineRule;
  lineRule: LineRule;
  saveLastValue?: boolean;
  defaultValue?: string;
  measure?: 'pt';
};

const CustomSpacingModal = () => {
  const intl = useIntl();

  const isOpen = useSelector((state) => state.modals.open.CustomSpacingModal);
  const spacing = useSelector((state) => state.modals.CustomSpacingModal.spacing);

  const lineSpacingTypeOptions: SpacingTypeOption[] = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ id: 'editor.menu.format.lineSpacing.single' }),
        value: '1',
        lineRule: 'auto',
        defaultValue: undefined,
      },
      {
        label: '1.15',
        value: '1.15',
        lineRule: 'auto',
        defaultValue: undefined,
      },
      {
        label: '1.5',
        value: '1.5',
        lineRule: 'auto',
        defaultValue: undefined,
      },
      {
        label: intl.formatMessage({ id: 'editor.menu.format.lineSpacing.double' }),
        value: '2',
        lineRule: 'auto',
        defaultValue: undefined,
      },
      {
        label: intl.formatMessage({ id: 'AT_LEAST' }),
        value: 'atLeast',
        lineRule: 'atLeast',
        measure: 'pt',
        defaultValue: '12',
        saveLastValue: true,
      },
      {
        label: intl.formatMessage({ id: 'storage.actionBar.search.rules.conditions.exactly' }),
        value: 'exact',
        lineRule: 'exact',
        measure: 'pt',
        defaultValue: '12',
        saveLastValue: true,
      },
      {
        label: intl.formatMessage({ id: 'MULTIPLE' }),
        value: 'auto',
        lineRule: 'auto',
        defaultValue: '1.33',
        saveLastValue: true,
      },
    ];
  }, []);

  const initialValues = useMemo(() => {
    let lineRule: SpacingTypeOption['value'] = spacing.lineRule ?? 'auto';
    let lineHeight: string | undefined =
      typeof spacing.lineHeight === 'number' ? `${spacing.lineHeight}` : spacing.lineHeight;

    switch (lineRule) {
      case 'auto': {
        switch (lineHeight) {
          case '1':
          case '1.15':
          case '1.5':
          case '2':
            lineRule = lineHeight;
            lineHeight = undefined;
            break;
        }
        break;
      }
    }
    return {
      lineRule,
      lineHeight,
    };
  }, [spacing]);

  const [state, setState] = useState<Editor.Edition.SpacingStylesMap>({
    ...spacing,
    lineHeight: initialValues.lineHeight,
  });
  const [selectedSpacingType, setSelectedSpacingType] = useState<SpacingTypeOption | undefined>(
    lineSpacingTypeOptions.find((option) => option.value === initialValues.lineRule),
  );
  const [lastValue, setLastValue] = useState<{ [x in SpacingTypeOption['value']]?: string }>({
    [initialValues.lineRule]: initialValues.lineHeight,
  });
  const dispatch = useDispatch();

  const handleBeforeAS = () => {
    setState((prevState: Editor.Edition.SpacingStylesMap) => ({
      ...prevState,
      asb: !state.asb,
    }));
  };
  const handleAfterAS = () => {
    setState((prevState: Editor.Edition.SpacingStylesMap) => ({
      ...prevState,
      asa: !state.asa,
    }));
  };

  const handleSelectedSpacingType: SelectProps<SpacingTypeOption>['onChange'] = (e) => {
    /**
     * When the selected spacing type changes:
     * Set the value of the line spacing input to its last value or default value or option value
     */
    if (e) {
      setState((prevState) => ({
        ...prevState,
        lineHeight: Number(lastValue?.[e?.value] ?? e?.defaultValue),
        lineRule: e?.lineRule,
      }));
      setSelectedSpacingType(e);
    }
  };

  const handleSpacingValueChange = (value: string) => {
    setState((prevState: Editor.Edition.SpacingStylesMap) => ({
      ...prevState,
      lineHeight: Number(value),
    }));

    /**
     * If a value is set and the selected spacing type is a auto preset option
     * set the selected option to the auto (multiple) option
     */
    if (value) {
      switch (selectedSpacingType?.lineRule) {
        case 'auto':
          setSelectedSpacingType(lineSpacingTypeOptions.find((option) => option.value === 'auto'));
      }

      /**
       * Save the last value if the selected spacing type option has this setting on
       */
      if (selectedSpacingType?.saveLastValue) {
        setLastValue((prevState) => ({
          ...prevState,
          [selectedSpacingType.value]: value,
        }));
      }
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    setState((prevState: Editor.Edition.SpacingStylesMap) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleApply = () => {
    let lineHeight = state.lineHeight;
    let lineRule = state.lineRule ?? 'auto';

    if (selectedSpacingType) {
      /**
       * When one of these spacing type are selected,
       * the lineHeight will be undefined in order to have the input empty
       * so we need to set the lineHeight to the value of the selected spacing type before submitting
       */
      switch (selectedSpacingType.value) {
        case '1':
        case '1.15':
        case '1.5':
        case '2': {
          lineHeight = Number(selectedSpacingType?.value);
          lineRule = 'auto';
          break;
        }
      }

      /**
       * If the lineHeight its not set, set it to the last value or default value of the selected spacing type
       * Needs this validation because the last value its only set when onBlur is made on the MeasureInput
       * and it can happen that the user deletes the value and clicks directly on the submit button
       * without making the onBlur event
       */
      if (lineHeight == null) {
        lineHeight = Number(
          lastValue?.[selectedSpacingType.value] ?? selectedSpacingType?.defaultValue,
        );
      }
    }

    EditorManager.getInstance().lineSpaceCurrentSelection({
      ...state,
      lineRule,
      lineHeight,
    });
    close();
  };

  const close = () => {
    dispatch(closeModal('CustomSpacingModal'));
  };

  return (
    <Modal open={!!isOpen} close={close} width="65rem" testId="custom-spacing">
      <Modal.Header>
        <FormattedMessage id="SPACING_OPTIONS" />
      </Modal.Header>
      <Modal.Body style={{ overflow: 'visible' }}>
        <div className={styles.section}>
          <SectionHeader size="m" testId="line-spacing">
            <FormattedMessage id="editor.modals.customSpacing.lineSpacing" />
          </SectionHeader>
        </div>
        <div className={styles.content}>
          <div className={styles.row}>
            <Select
              size="l"
              variant="standard"
              options={lineSpacingTypeOptions}
              value={lineSpacingTypeOptions.find(
                (option) => option.value === selectedSpacingType?.value,
              )}
              onChange={handleSelectedSpacingType}
              width="26.5rem"
              menuPosition="fixed"
              menuWidth="26.5rem"
              isClearable={false}
              testId="line-spacing-type"
            />
            <MeasureInput
              size="l"
              name="lineHeight"
              onChange={handleSpacingValueChange}
              value={state.lineHeight ? `${state.lineHeight}` : ''}
              step="0.1"
              valueSuffix={selectedSpacingType?.measure}
              min={0}
              avoidEmpty
              placeholder={''}
              testId="custom-spacing-line-spacing"
              width="26.5rem"
            />
          </div>
        </div>
        <div className={styles.section}>
          <SectionHeader size="m" testId="paragraph-spacing">
            <FormattedMessage id="PARAGRAPH_SPACING" />
          </SectionHeader>
        </div>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.column}>
              <InputField
                size="m"
                label={intl.formatMessage({ id: 'editor.modals.customSpacing.spacingBefore' })}
                testId="space-before"
                style={{ marginBottom: '1.25rem' }}
              >
                <NumberInput
                  size="l"
                  name="spaceBefore"
                  onChange={onInputChange}
                  value={`${state.spaceBefore}`}
                  step="1"
                  min="0"
                  disabled={!!state.asb}
                  placeholder=""
                  testId="custom-spacing-space-before"
                  isClearVisible
                  width="26.5rem"
                />
              </InputField>
              <Checkbox
                onChange={handleBeforeAS}
                checked={!!state.asb}
                size="s"
                testId="custom-spacing-auto-spacing"
              >
                <FormattedMessage id="AUTO_SPACING" />
              </Checkbox>
            </div>
            <div className={styles.column}>
              <InputField
                size="m"
                label={intl.formatMessage({ id: 'editor.modals.customSpacing.spacingAfter' })}
                testId="space-after"
                style={{ marginBottom: '1.25rem' }}
              >
                <NumberInput
                  size="l"
                  name="spaceAfter"
                  onChange={onInputChange}
                  value={`${state.spaceAfter}`}
                  step="1"
                  min="0"
                  placeholder=""
                  disabled={!!state.asa}
                  testId="custom-spacing-space-after"
                  isClearVisible
                  width="26.5rem"
                />
              </InputField>
              <Checkbox
                onChange={handleAfterAS}
                checked={!!state.asa}
                size="s"
                testId="custom-spacing-auto-spacing"
              >
                <FormattedMessage id="AUTO_SPACING" />
              </Checkbox>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="standard" size="m" onClick={close} testId="custom-spacing-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="primary"
          onClick={handleApply}
          testId="custom-spacing-submit"
          style={{ margin: '0 0 0 1rem' }}
        >
          <FormattedMessage id="APPLY_CHANGES" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomSpacingModal;
